/**
 * Portfolio Hook
 * Manages portfolio state and data fetching
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchUserPositions } from '../services/portfolioService';
import { calculatePortfolioSummary } from '../helpers/portfolioCalculations';

/**
 * Hook to fetch and manage user portfolio data
 * @param {string} walletAddress - Connected wallet address
 * @returns {object} Portfolio state and actions
 */
export function usePortfolio(walletAddress) {
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState({
    totalRealizedPnL: 0,
    totalGames: 0,
    openPositions: 0,
    closedPositions: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadPositions = useCallback(async () => {
    if (!walletAddress) {
      setPositions([]);
      setSummary({
        totalRealizedPnL: 0,
        totalGames: 0,
        openPositions: 0,
        closedPositions: 0,
      });
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await fetchUserPositions(walletAddress);
      setPositions(data);

      // Calculate summary metrics
      const summaryMetrics = calculatePortfolioSummary(data);
      setSummary(summaryMetrics);
    } catch (err) {
      console.error('Failed to load portfolio:', err);
      setError(err?.message || 'Failed to load portfolio');
      setPositions([]);
      setSummary({
        totalRealizedPnL: 0,
        totalGames: 0,
        openPositions: 0,
        closedPositions: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [walletAddress]);

  // Load positions when wallet changes
  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  return {
    positions,
    summary,
    loading,
    error,
    refresh: loadPositions,
  };
}
